import { WatchQueryFetchPolicy } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { getEqlPropsForMetadataCoverage } from '@modules/integrationsV2/utils';
import {
  IntegrationType,
  MetadataDetailsViewFragment,
} from 'generated/graphql';
import { useMemo, useState } from 'react';
import useBatchExecuteEQL from 'v2/core/repository/edge-server/eql/useBatchExecuteEQL';
import {
  BatchExecuteEQLResponse,
  IsLoading,
} from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

interface FetchCoverageForMultipleMetadataFieldsParams {
  metadataFields: MetadataDetailsViewFragment[];
  metadataIntegrationType: IntegrationType;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export const useFetchCoverageForMultipleMetadataFields = ({
  metadataFields,
  metadataIntegrationType,
  fetchPolicy = 'cache-and-network',
}: FetchCoverageForMultipleMetadataFieldsParams) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const [valuesMap, setValuesMap] = useState<Record<string, number>>({});
  const [hasRetriedOnError, setHasRetriedOnError] = useState(false);

  const eqlBatches = useMemo(() => {
    // Filter out the metadata fields that already have coverage values
    const filteredMetadataFields = metadataFields.filter(
      (metadataField) => !valuesMap[metadataField.key],
    );

    return filteredMetadataFields.map((metadataField) => ({
      id: metadataField.key,
      eqlProps: getEqlPropsForMetadataCoverage(
        metadataField,
        metadataIntegrationType,
      ),
    }));
  }, [metadataFields, metadataIntegrationType, valuesMap]);

  const { refetch, data, error, status } = useBatchExecuteEQL(
    {
      tenantID,
      batches: eqlBatches,
    },
    {
      Skip: !eqlBatches.length,
      FetchPolicy: fetchPolicy,
      onCompleted: (data) => {
        if (!data.batchExecuteEQL?.results) {
          return;
        }

        if (hasRetriedOnError) {
          setHasRetriedOnError(false);
        }

        const response = data.batchExecuteEQL.results.reduce(
          (response: BatchExecuteEQLResponse, result) => {
            response[result.queryID] = result.result;
            return response;
          },
          {} as BatchExecuteEQLResponse,
        );

        const obj: Record<string, number> = {};

        eqlBatches.forEach((batch) => {
          const data = response?.[batch.id];
          if (data?.__typename === 'GetAggregatesResponse') {
            const value = data.data[0]?.value ?? 0;
            const formattedValue = parseFloat(value.toFixed(1));
            obj[batch.id] = formattedValue;
          }
        });

        setValuesMap((prev) => ({ ...prev, ...obj }));
      },
      onError: () => {
        const shouldRetry = !hasRetriedOnError && refetch;

        if (shouldRetry) {
          setHasRetriedOnError(true);
          refetch();
        }

        showErrorNotification({
          contentComponent: `Failed to fetch coverage data. ${
            shouldRetry ? 'Retrying...' : ''
          }`,
          props: {},
        });
      },
    },
  );

  return {
    valuesMap,
    error,
    isLoading: data ? false : IsLoading(status),
    status,
  };
};
